<template>
  <div class="c-upload-file w-full box-border select-none" :class="{ '!w-full': file }">
    <CCard v-if="file" embedded>
      <div class="flex items-center h-14">
        <CFileThumbnail
          class="w-16 h-10 flex flex-shrink-0 items-center justify-center"
          :height="40"
          :icon-size="24"
          :item="file as unknown as RenderableFile"
          :width="60"
        />

        <div v-if="!selectedPageIndex" class="flex-grow flex flex-col gap-1 mx-2 min-w-0 w-full leading-none">
          <div class="title text-ellipsis whitespace-nowrap font-700 overflow-hidden">
            {{ file.name }}
          </div>
          <div v-if="!hideContentType" class="text-ellipsis whitespace-nowrap color-text2 truncate font-size-[12px]">
            <component :is="renderContentType(file.content_type as FileContentTypeEnum)" class="color-primary3" />
          </div>
        </div>

        <div v-else class="flex-grow flex flex-col gap-1 mx-2 min-w-0 w-full leading-none">
          <div class="title text-ellipsis whitespace-nowrap font-700 overflow-hidden">
            {{ t('canvasUI.components.CSingleFileSelector.pageName', { number: selectedPageIndex + 1 }) }}
          </div>
          <div class="description text-ellipsis whitespace-nowrap overflow-hidden">
            {{ file.name }}
          </div>
        </div>

        <div class="flex justify-end gap-2">
          <CButton
            circle
            class="flex-shrink-0"
            :color="themeVars.primary5"
            :size="size"
            :text-color="themeVars.primary"
            @click="clearSelectedFile"
          >
            <CIcon icon="close" :size="iconSize" />
          </CButton>
          <CButton
            circle
            class="flex-shrink-0"
            :color="themeVars.primary5"
            :size="size"
            :text-color="themeVars.primary"
            @click="openContentSelector"
          >
            <CIcon icon="search" :size="iconSize" />
          </CButton>
        </div>
      </div>
    </CCard>
    <CButton
      v-else
      :class="uploadButtonClass"
      icon-placement="left"
      :loading="isSelecting"
      :size="size"
      @click="openContentSelector"
    >
      <template #icon><CIcon icon="search" :size="iconSize" /></template>
      {{ t('canvasUI.components.CSingleFileSelector.selectFile') }}
    </CButton>
  </div>
</template>

<script setup lang="ts">
import type { Size } from 'naive-ui/es/button/src/interface'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useThemeVars from '@lib/composables/useThemeVars'
import type { RenderableFile } from '@lib/types/files'
import type { File, FileContentTypeEnum } from '@openapi'

const props = withDefaults(
  defineProps<{
    file: (Partial<File> & { page_index?: number }) | null
    hideContentType?: boolean
    isSelecting?: boolean
    selectedPageIndex?: number
    size?: Size
    uploadButtonClass?: string
  }>(),
  {
    hideContentType: false,
    isSelecting: false,
    selectedPageIndex: 0,
    size: 'medium',
    uploadButtonClass: '',
  },
)

const emit = defineEmits<{
  clearSelectedFile: []
  openContentSelector: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const { renderContentType } = useFileDisplayHelpers()

const iconSize = computed(() => {
  return props.size === 'small' ? '14px' : '16px'
})

const clearSelectedFile = () => {
  emit('clearSelectedFile')
}

const openContentSelector = () => {
  emit('openContentSelector')
}
</script>
